import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaStar, FaCheckCircle, FaWrench, FaCalendarAlt, FaChevronDown } from "react-icons/fa";
import api from "../utils/api";
import { useVehicle } from "../contexts/VehicleContext";

const SERVICE_PILLS = [
  "All Services",
  "Periodic Service",
  "Brakes",
  "AC Service",
  "Clutch",
  "Suspension",
  "Detailing",
  "Emergency Repair",
];

const SECONDARY_CHIPS = ["Pickup/Drop", "Open Today", "Rating 4+", "Verified only", "GST invoice"];

const FILTER_SERVICE_TYPES = [
  { id: "svc_brake", label: "Brake Replacement" },
  { id: "svc_periodic", label: "Periodic Service" },
  { id: "svc_ac", label: "AC Gas Refill" },
  { id: "svc_clutch", label: "Clutch Overhaul" },
  { id: "svc_susp", label: "Suspension Work" },
];

const FILTER_LOCATIONS = [
  { id: "loc_gurugram", label: "Gurugram" },
  { id: "loc_delhi", label: "Delhi NCR" },
];

const FILTER_PICKUP = [{ id: "pickup_avail", label: "Available" }];

const FILTER_RATINGS = [
  { id: "rate_4", label: "4 ★ & above" },
  { id: "rate_3", label: "3 ★ & above" },
];

const FILTER_TURNAROUND = [
  { id: "turn_same", label: "Same Day" },
  { id: "turn_24", label: "Within 24 hrs" },
];

const MOCK_GARAGES = [
  { id: "m1", name: "AutoPro Service Centre", area: "Sector 29, Gurugram", dist: "2.1 km", until: "8:00 pm", from: 1200, rating: 4.8, reviews: 214 },
  { id: "m2", name: "CityLine Motors", area: "DLF Phase 3, Gurugram", dist: "3.4 km", until: "7:30 pm", from: 980, rating: 4.6, reviews: 132 },
  { id: "m3", name: "RapidFix Garage", area: "Old Gurgaon Road", dist: "4.0 km", until: "9:00 pm", from: 1450, rating: 4.9, reviews: 401 },
  { id: "m4", name: "Elite Auto Care", area: "Sohna Road", dist: "5.2 km", until: "6:00 pm", from: 2100, rating: 4.7, reviews: 88 },
  { id: "m5", name: "DriveWell Workshop", area: "Cyber City", dist: "1.8 km", until: "8:30 pm", from: 1100, rating: 4.5, reviews: 167 },
];

const TIME_SLOTS = ["9:00", "10:00", "11:00", "12:00", "2:00", "3:00"];

/** Sort dropdown — labels match garages results toolbar reference. */
const SORT_OPTIONS = [
  { id: "best_match", menuLabel: "Sort: Best Match", triggerLabel: "Best Match" },
  { id: "rating", menuLabel: "Rating", triggerLabel: "Rating" },
  { id: "price", menuLabel: "Price", triggerLabel: "Price" },
  { id: "distance", menuLabel: "Distance", triggerLabel: "Distance" },
];

function parseDistKm(dist) {
  const m = String(dist ?? "").match(/([\d.]+)/);
  return m ? parseFloat(m[1]) : Number.POSITIVE_INFINITY;
}

function FilterCheck({ checked, onToggle, label }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onToggle}
      className="flex w-full items-start gap-2.5 rounded-lg py-1 text-left transition hover:bg-white/[0.03]"
    >
      <span
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border transition ${
          checked
            ? "border-[#F49B1F] bg-[#F49B1F] shadow-[0_0_0_1px_rgba(244,155,31,0.35)]"
            : "border-slate-500 bg-transparent"
        }`}
      >
        {checked && (
          <svg className="h-2.5 w-2.5 text-black" viewBox="0 0 12 10" fill="none" aria-hidden>
            <path d="M1 5l3.5 3.5L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className="text-[13px] leading-snug text-slate-200">{label}</span>
    </button>
  );
}

function FilterSectionTitle({ children }) {
  return (
    <p className="mb-2.5 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{children}</p>
  );
}

function seedFromId(id) {
  const s = String(id ?? "0");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function GarageResultCard({ garage, selected, onSelectBook }) {
  const tags = ["Brake Replacement", "Full Service"].map((t, i) => (
    <span
      key={t}
      className={`inline-flex rounded-md border px-2 py-0.5 text-[10px] font-semibold sm:text-xs ${
        i === 0
          ? "border-sky-500/40 bg-sky-950/40 text-sky-200"
          : "border-amber-500/40 bg-amber-950/30 text-amber-100"
      }`}
    >
      {t}
    </span>
  ));

  return (
    <div
      className={`flex flex-col gap-4 rounded-2xl border p-4 shadow-lg transition-all sm:flex-row sm:items-stretch sm:gap-5 sm:p-5 ${
        selected
          ? "border-amber-500/50 bg-[#121622] shadow-[inset_0_0_0_1px_rgba(245,166,35,0.25),0_12px_40px_-12px_rgba(0,0,0,0.65)]"
          : "border-slate-700/70 bg-[#11141d] shadow-[0_8px_28px_-8px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.04)] hover:border-slate-600"
      }`}
    >
      <div className="flex shrink-0 gap-3 sm:flex-col sm:items-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-slate-800/90 ring-1 ring-slate-600/50 sm:h-16 sm:w-16">
          <FaWrench className="text-xl text-amber-400/90 sm:text-2xl" />
        </div>
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-400 sm:flex-col sm:text-xs">
          <FaCheckCircle className="text-emerald-400" />
          Verified
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <h3 className="text-base font-bold text-white sm:text-lg">{garage.name}</h3>
        <p className="mt-1 text-xs text-slate-400 sm:text-sm">
          {garage.area} · {garage.dist} · Open until {garage.until}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">{tags}</div>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1 text-amber-300/95">
            <FaStar className="text-amber-400" />
            {Number(garage.rating ?? 0).toFixed(1)}
          </span>
          <span>({garage.reviews} reviews)</span>
        </div>
        <div className="mt-2 grid gap-1 text-[11px] text-slate-500 sm:grid-cols-2 sm:text-xs">
          <span>Pickup/Drop: Yes</span>
          <span>Turnaround: 4–6 hrs</span>
          <span className="sm:col-span-2">GST Invoice: Yes</span>
        </div>
      </div>

      <div className="flex shrink-0 flex-row items-center justify-between gap-3 border-t border-slate-800/80 pt-3 sm:w-44 sm:flex-col sm:border-t-0 sm:pt-0 sm:text-right">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">From</p>
          <p className="text-lg font-bold text-white sm:text-xl">₹{garage.from.toLocaleString("en-IN")}</p>
        </div>
        <div className="flex flex-col gap-2 sm:w-full">
          <button
            type="button"
            onClick={() => onSelectBook(garage)}
            className="rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 px-4 py-2.5 text-xs font-bold text-amber-950 shadow-lg shadow-amber-900/30 transition hover:from-amber-300 hover:to-orange-400 sm:text-sm"
          >
            Book Service
          </button>
          <button type="button" className="text-center text-xs font-semibold text-sky-400 hover:text-sky-300">
            Compare
          </button>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_FILTERS = {
  svc_brake: true,
  svc_periodic: false,
  svc_ac: false,
  svc_clutch: false,
  svc_susp: false,
  loc_gurugram: true,
  loc_delhi: false,
  pickup_avail: true,
  rate_4: true,
  rate_3: false,
  turn_same: false,
  turn_24: true,
};

export default function GarageServicesDirectory() {
  const { selectedVehicle } = useVehicle();
  const [garages, setGarages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePill, setActivePill] = useState("All Services");
  const [selectedGarage, setSelectedGarage] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState("12:00");
  const [pickup, setPickup] = useState(true);
  const [useMyParts, setUseMyParts] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [topTags, setTopTags] = useState([
    { id: "gurugram", label: "Gurugram" },
    { id: "brakes", label: "Brakes" },
  ]);
  const [secondaryChipOn, setSecondaryChipOn] = useState({});
  const [sortBy, setSortBy] = useState("best_match");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef(null);
  const [serviceTypeInput, setServiceTypeInput] = useState("Brake service");
  const [vehicleInput, setVehicleInput] = useState("");

  const toggleFilter = useCallback((id) => {
    setFilters((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const bookingTotals = useMemo(() => {
    const labour = 1200;
    const parts = 400;
    const sub = labour + parts;
    const gst = Math.round(sub * 0.18);
    const pickupFee = 0;
    const total = sub + gst + pickupFee;
    return { labour, parts, sub, gst, pickupFee, total };
  }, []);

  const vehicleLabel = useMemo(() => {
    if (!selectedVehicle) return "Maruti Swift VXI 2022";
    const { make, model, year, variant } = selectedVehicle;
    return [make, model, variant, year].filter(Boolean).join(" ");
  }, [selectedVehicle]);

  useEffect(() => {
    setVehicleInput(vehicleLabel);
  }, [vehicleLabel]);

  const sortedGarages = useMemo(() => {
    const list = [...garages];
    if (sortBy === "rating") {
      list.sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
    } else if (sortBy === "price") {
      list.sort((a, b) => (a.from ?? 0) - (b.from ?? 0));
    } else if (sortBy === "distance") {
      list.sort((a, b) => parseDistKm(a.dist) - parseDistKm(b.dist));
    }
    return list;
  }, [garages, sortBy]);

  useEffect(() => {
    if (!sortMenuOpen) return;
    const onDoc = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
        setSortMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [sortMenuOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/garages");
        const raw = res?.data?.data?.garages || res?.data?.garages || [];
        const mapped = raw.length
          ? raw.map((g, i) => {
              const id = g.id || g._id || `g${i}`;
              const seed = seedFromId(id);
              return {
                id,
                name: g.name || "Garage",
                area: g.location || "Gurugram",
                dist: `${(1.5 + (seed % 50) / 10).toFixed(1)} km`,
                until: ["6:00 pm", "7:30 pm", "8:00 pm", "8:30 pm", "9:00 pm"][seed % 5],
                from: 800 + (seed % 40) * 50,
                rating: 4.3 + (seed % 7) / 10,
                reviews: 40 + (seed % 400),
              };
            })
          : MOCK_GARAGES;
        if (!cancelled) setGarages(mapped);
      } catch {
        if (!cancelled) setGarages(MOCK_GARAGES);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSelectBook = useCallback((g) => {
    setSelectedGarage(g);
  }, []);

  return (
    <div className="min-h-screen bg-[#0b0e14] text-slate-200">
      <div className="border-b border-slate-800/80 bg-[#080b12] px-4 pt-6 pb-4 sm:px-6 sm:pt-8 sm:pb-5 md:px-8 lg:px-8">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-500/90">Sparepé · Garage Services</p>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl md:text-4xl">
          Find trusted workshops near you
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400 sm:text-base">
          Compare garages, read reviews, and book brake service, periodic maintenance, and more — with pickup &amp; drop
          options.
        </p>

        <div className="mt-5 flex w-full flex-col gap-3 sm:mt-6 lg:flex-row lg:items-end lg:gap-4">
          <div className="grid flex-1 gap-2 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Service type</span>
              <input
                type="text"
                value={serviceTypeInput}
                onChange={(e) => setServiceTypeInput(e.target.value)}
                placeholder="e.g. Brake service, Periodic service"
                className="w-full rounded-xl border border-slate-700 bg-[#11141d] px-3 py-2.5 text-sm text-white outline-none placeholder:text-slate-600 focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Vehicle</span>
              <input
                type="text"
                value={vehicleInput}
                onChange={(e) => setVehicleInput(e.target.value)}
                placeholder="Make, model, variant, year"
                className="w-full rounded-xl border border-slate-700 bg-[#11141d] px-3 py-2.5 text-sm text-white outline-none placeholder:text-slate-600 focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/20"
              />
            </label>
            <label className="block sm:col-span-1">
              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Location</span>
              <input
                defaultValue="Gurugram, HR"
                className="w-full rounded-xl border border-slate-700 bg-[#11141d] px-3 py-2.5 text-sm text-white outline-none placeholder:text-slate-600 focus:border-amber-500/50"
                placeholder="City or area"
              />
            </label>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-xl border border-amber-300/50 bg-gradient-to-r from-amber-400 via-amber-500 to-orange-500 px-8 py-3 text-sm font-bold text-amber-950 shadow-[0_4px_28px_-2px_rgba(251,146,60,0.55)] ring-2 ring-amber-400/70 ring-offset-2 ring-offset-[#080b12] transition hover:from-amber-300 hover:to-orange-400 hover:shadow-[0_6px_32px_-2px_rgba(251,146,60,0.65)] hover:ring-amber-300/80 active:scale-[0.98] active:brightness-95 lg:mb-0"
          >
            Find Garages
          </button>
        </div>

        <div className="mt-4 flex w-full gap-2 overflow-x-auto pb-1 sm:mt-5">
          {SERVICE_PILLS.map((pill) => (
            <button
              key={pill}
              type="button"
              onClick={() => setActivePill(pill)}
              className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold transition sm:text-sm ${
                activePill === pill
                  ? "border-amber-500/60 bg-amber-500/15 text-amber-200"
                  : "border-slate-700/80 bg-[#11141d] text-slate-300 hover:border-slate-600"
              }`}
            >
              {pill === "Emergency Repair" && <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-red-500 shadow-[0_0_8px_#ef4444]" aria-hidden />}
              {pill}
            </button>
          ))}
        </div>

        <hr className="mt-4 w-full border-0 border-t border-slate-700/80 sm:mt-5" />

        <div className="mt-3 flex w-full flex-wrap items-center gap-2 sm:mt-4">
          {topTags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#F49B1F]/70 bg-[#F49B1F]/10 px-3 py-1.5 text-xs font-semibold text-amber-100 shadow-[0_0_12px_rgba(244,155,31,0.12)]"
            >
              {tag.label}
              <button
                type="button"
                className="relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-amber-200/90 hover:bg-black/20 hover:text-white"
                aria-label={`Remove ${tag.label}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setTopTags((t) => t.filter((x) => x.id !== tag.id));
                }}
              >
                ×
              </button>
            </span>
          ))}
          {SECONDARY_CHIPS.map((c) => {
            const on = !!secondaryChipOn[c];
            if (on) {
              return (
                <span
                  key={c}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#F49B1F]/70 bg-[#F49B1F]/10 px-3 py-1.5 text-xs font-semibold text-amber-100 shadow-[0_0_12px_rgba(244,155,31,0.12)]"
                >
                  {c}
                  <button
                    type="button"
                    className="relative z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-amber-200/90 hover:bg-black/20 hover:text-white"
                    aria-label={`Remove ${c}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSecondaryChipOn((s) => ({ ...s, [c]: false }));
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            }
            return (
              <button
                key={c}
                type="button"
                onClick={() => setSecondaryChipOn((s) => ({ ...s, [c]: true }))}
                className="rounded-full border border-slate-700/90 bg-[#0f1219] px-3 py-1.5 text-xs font-medium text-slate-400 transition hover:border-slate-600 hover:text-slate-200"
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mx-auto grid max-w-[1600px] gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[minmax(0,17.5rem)_1fr_minmax(0,20rem)] lg:items-start lg:gap-8 lg:px-8">
        {/* Left filter sidebar — sticky + scrollable (same as catalog) */}
        <aside className="order-2 w-full max-w-md self-start lg:sticky lg:top-28 lg:z-20 lg:order-none lg:max-w-none lg:max-h-[calc(100vh-8rem)]">
          <div className="max-h-[calc(100vh-8rem)] overflow-y-auto overscroll-contain rounded-2xl border border-slate-800/70 bg-[#12121a] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] [scrollbar-width:thin]">
          <div className="space-y-5">
            <div>
              <FilterSectionTitle>Service type</FilterSectionTitle>
              <div className="space-y-1">
                {FILTER_SERVICE_TYPES.map((o) => (
                  <FilterCheck
                    key={o.id}
                    checked={!!filters[o.id]}
                    onToggle={() => toggleFilter(o.id)}
                    label={o.label}
                  />
                ))}
              </div>
            </div>
            <div className="border-t border-slate-800/80 pt-4">
              <FilterSectionTitle>Location</FilterSectionTitle>
              <div className="space-y-1">
                {FILTER_LOCATIONS.map((o) => (
                  <FilterCheck
                    key={o.id}
                    checked={!!filters[o.id]}
                    onToggle={() => toggleFilter(o.id)}
                    label={o.label}
                  />
                ))}
              </div>
            </div>
            <div className="border-t border-slate-800/80 pt-4">
              <FilterSectionTitle>Pickup / drop</FilterSectionTitle>
              <div className="space-y-1">
                {FILTER_PICKUP.map((o) => (
                  <FilterCheck
                    key={o.id}
                    checked={!!filters[o.id]}
                    onToggle={() => toggleFilter(o.id)}
                    label={o.label}
                  />
                ))}
              </div>
            </div>
            <div className="border-t border-slate-800/80 pt-4">
              <FilterSectionTitle>Price range</FilterSectionTitle>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  placeholder="₹ Min"
                  className="w-full rounded-lg border border-slate-600/90 bg-[#0c0e14] px-2.5 py-2 text-xs text-white placeholder:text-slate-600 focus:border-[#F49B1F]/50 focus:outline-none focus:ring-1 focus:ring-[#F49B1F]/25"
                />
                <input
                  type="text"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  placeholder="₹ Max"
                  className="w-full rounded-lg border border-slate-600/90 bg-[#0c0e14] px-2.5 py-2 text-xs text-white placeholder:text-slate-600 focus:border-[#F49B1F]/50 focus:outline-none focus:ring-1 focus:ring-[#F49B1F]/25"
                />
              </div>
            </div>
            <div className="border-t border-slate-800/80 pt-4">
              <FilterSectionTitle>Rating</FilterSectionTitle>
              <div className="space-y-1">
                {FILTER_RATINGS.map((o) => (
                  <FilterCheck
                    key={o.id}
                    checked={!!filters[o.id]}
                    onToggle={() => toggleFilter(o.id)}
                    label={o.label}
                  />
                ))}
              </div>
            </div>
            <div className="border-t border-slate-800/80 pt-4">
              <FilterSectionTitle>Turnaround</FilterSectionTitle>
              <div className="space-y-1">
                {FILTER_TURNAROUND.map((o) => (
                  <FilterCheck
                    key={o.id}
                    checked={!!filters[o.id]}
                    onToggle={() => toggleFilter(o.id)}
                    label={o.label}
                  />
                ))}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="mt-6 w-full rounded-xl border border-amber-400/45 bg-gradient-to-b from-[#F5B041] via-[#F49B1F] to-[#E8890C] py-3 text-sm font-bold text-black shadow-[0_4px_24px_-2px_rgba(244,155,31,0.5)] ring-2 ring-amber-400/65 ring-offset-2 ring-offset-[#12121a] transition hover:brightness-105 hover:shadow-[0_6px_28px_-2px_rgba(244,155,31,0.6)] hover:ring-amber-300/75 active:scale-[0.98] active:brightness-95"
          >
            Apply
          </button>
          </div>
        </aside>

        {/* Results */}
        <main className="order-1 min-w-0 lg:order-none">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-400">
              <span className="font-semibold text-white">{loading ? "…" : garages.length}</span> garages near Gurugram for
              brake service
            </p>
            <div className="relative shrink-0" ref={sortMenuRef}>
              <button
                type="button"
                onClick={() => setSortMenuOpen((o) => !o)}
                className="inline-flex min-w-[10.5rem] items-center justify-between gap-2 rounded-lg border border-slate-600 bg-[#11141d] px-3 py-2 text-left text-xs font-medium text-white shadow-sm transition hover:border-slate-500 hover:bg-[#151922]"
                aria-expanded={sortMenuOpen}
                aria-haspopup="listbox"
              >
                <span className="text-slate-200">
                  Sort:{" "}
                  <span className="text-white">
                    {(SORT_OPTIONS.find((o) => o.id === sortBy) || SORT_OPTIONS[0]).triggerLabel}
                  </span>
                </span>
                <FaChevronDown
                  className={`shrink-0 text-[10px] text-slate-400 transition-transform duration-200 ${
                    sortMenuOpen ? "rotate-180" : ""
                  }`}
                  aria-hidden
                />
              </button>
              {sortMenuOpen ? (
                <ul
                  className="absolute right-0 z-50 mt-1 min-w-full whitespace-nowrap rounded-lg border border-slate-500/90 bg-[#0f1118] py-1 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.75)]"
                  role="listbox"
                  aria-label="Sort results"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <li key={opt.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={sortBy === opt.id}
                        className={`w-full px-3 py-2.5 text-left text-xs font-medium transition ${
                          sortBy === opt.id
                            ? "bg-slate-800/90 text-white"
                            : "text-slate-100 hover:bg-sky-600 hover:text-white"
                        }`}
                        onClick={() => {
                          setSortBy(opt.id);
                          setSortMenuOpen(false);
                        }}
                      >
                        {opt.menuLabel}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
          <div className="space-y-4">
            {loading ? (
              <p className="text-sm text-slate-500">Loading garages…</p>
            ) : (
              sortedGarages.map((g) => (
                <GarageResultCard
                  key={g.id}
                  garage={g}
                  selected={selectedGarage?.id === g.id}
                  onSelectBook={onSelectBook}
                />
              ))
            )}
          </div>
        </main>

        {/* Booking panel */}
        <aside className="order-3 lg:sticky lg:top-24 lg:order-none">
          <div className="rounded-2xl border border-slate-800/60 bg-[#16182a] p-4 shadow-xl sm:p-5">
            {selectedGarage ? (
              <>
                <h2 className="text-base font-bold leading-snug text-white sm:text-lg">
                  Book {selectedGarage.name}
                </h2>
                <label className="mt-5 block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Vehicle</span>
                  <select className="mt-1.5 w-full rounded-xl border border-slate-600/80 bg-[#0f1118] px-3 py-2.5 text-sm text-white focus:border-[#F49B1F]/45 focus:outline-none focus:ring-1 focus:ring-[#F49B1F]/20">
                    <option>{vehicleInput.trim() || vehicleLabel}</option>
                  </select>
                </label>
                <label className="mt-4 block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Service</span>
                  <select className="mt-1.5 w-full rounded-xl border border-slate-600/80 bg-[#0f1118] px-3 py-2.5 text-sm text-white focus:border-[#F49B1F]/45 focus:outline-none focus:ring-1 focus:ring-[#F49B1F]/20">
                    <option>Brake Pad Replacement (Front)</option>
                    <option>Brake disc skimming</option>
                    <option>Full brake inspection</option>
                  </select>
                </label>
                <label className="mt-4 block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Date</span>
                  <div className="relative mt-1.5">
                    <input
                      type="date"
                      defaultValue="2025-02-14"
                      className="w-full rounded-xl border border-slate-600/80 bg-[#0f1118] py-2.5 pl-3 pr-10 text-sm text-white focus:border-[#F49B1F]/45 focus:outline-none focus:ring-1 focus:ring-[#F49B1F]/20"
                    />
                    <FaCalendarAlt className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-500" aria-hidden />
                  </div>
                </label>
                <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Time slot</p>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSelectedSlot(t)}
                      className={`rounded-lg border py-2.5 text-xs font-semibold transition ${
                        selectedSlot === t
                          ? "border-[#F49B1F] bg-[#F49B1F]/10 text-[#F49B1F] shadow-[0_0_14px_rgba(244,155,31,0.2)]"
                          : "border-slate-600/80 bg-[#0f1118] text-slate-200 hover:border-slate-500"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <div className="mt-5 space-y-4 border-t border-slate-700/60 pt-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-200">Pickup &amp; Drop-off</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={pickup}
                      onClick={() => setPickup(!pickup)}
                      className={`relative h-7 w-12 shrink-0 rounded-full transition ${pickup ? "bg-[#F49B1F]" : "bg-slate-600"}`}
                    >
                      <span
                        className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition ${pickup ? "left-5" : "left-0.5"}`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm leading-snug text-slate-200">Use my parts (Sparepé order)</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={useMyParts}
                      onClick={() => setUseMyParts(!useMyParts)}
                      className={`relative h-7 w-12 shrink-0 rounded-full transition ${useMyParts ? "bg-[#F49B1F]" : "bg-slate-600"}`}
                    >
                      <span
                        className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition ${useMyParts ? "left-5" : "left-0.5"}`}
                      />
                    </button>
                  </div>
                </div>
                <div className="mt-5 space-y-2 border-t border-slate-700/60 pt-4 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>Labour</span>
                    <span className="text-slate-200">₹{bookingTotals.labour.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Parts</span>
                    <span className="text-slate-200">₹{bookingTotals.parts.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>GST (18%)</span>
                    <span className="text-slate-200">₹{bookingTotals.gst.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Pickup fee</span>
                    <span className="text-slate-200">₹{bookingTotals.pickupFee}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-700/50 pt-3 text-base font-bold">
                    <span className="text-slate-200">Total</span>
                    <span className="text-[#F5C842]">₹{bookingTotals.total.toLocaleString("en-IN")}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="mt-5 w-full rounded-xl py-3 text-sm font-bold text-black shadow-lg transition hover:brightness-110"
                  style={{ backgroundColor: "#F49B1F" }}
                >
                  Confirm Booking →
                </button>
                <button
                  type="button"
                  className="mt-3 w-full rounded-xl border border-slate-500/80 bg-transparent py-2.5 text-sm font-semibold text-white transition hover:border-slate-400 hover:bg-white/[0.04]"
                >
                  Request Callback
                </button>
              </>
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm font-medium text-slate-400">Select a garage</p>
                <p className="mt-2 text-xs text-slate-600">
                  Click <span className="text-amber-400/90">Book Service</span> on a workshop to fill this form.
                </p>
              </div>
            )}
            <Link to="/" className="mt-4 block text-center text-xs font-semibold text-sky-400 hover:text-sky-300">
              ← Back to home
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
